/*
===============================================================================================================
                                 Copyright 2025-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable no-console */
import { includes, some } from 'lodash';
import { PERMISSIONS_ENUM } from './constants';
import { isEmpty } from '../validation';
import luiPortalUtilitiesManager from '../lui-portal-utilities-manager';

export const ROLE_TYPES = {
  ADMIN: 'admin',
  MERCHANT: 'merchant',
  UNKNOWN: 'unknown',
};

export const RBAC_PERMISSIONS = PERMISSIONS_ENUM;

/**
 * Determines the user's role type based on their assigned roles and allowed roles configuration.
 *
 * @param {string[]} roles - Array of role strings assigned to the user
 * @param {Object} allowedRolesConfig - Configuration object containing allowed role definitions
 * @param {string[]} allowedRolesConfig.adminRoles - Array of admin role identifiers
 * @param {string[]} allowedRolesConfig.merchantRoles - Array of merchant role identifiers
 * @returns {string} The user's role type
 */
export const getUserRoleType = (roles, allowedRolesConfig) => {
  const isAdmin = some(allowedRolesConfig.adminRoles, (adminRole) => includes(roles, adminRole));

  const isMerchant = some(allowedRolesConfig.merchantRoles, (merchantRole) => includes(roles, merchantRole));

  if (!isAdmin && !isMerchant) {
    return ROLE_TYPES.UNKNOWN;
  }

  if (isAdmin && isMerchant) {
    return ROLE_TYPES.UNKNOWN;
  }

  if (isAdmin) {
    return ROLE_TYPES.ADMIN;
  }

  if (isMerchant) {
    return ROLE_TYPES.MERCHANT;
  }

  return ROLE_TYPES.UNKNOWN;
};

/**
 * Validates whether a user has a specific permission.
 *
 * @param {Object} params - Validation parameters
 * @param {string[]} params.userPermissions - Array of permission strings assigned to the user
 * @param {string} params.permissionNameSpace - Namespace prefix for the permission (e.g., 'admin', 'user')
 * @param {string} params.permissionToValidate - Permission to validate against
 * @returns {boolean} Returns true if the user has the specified permission, false otherwise
 *
 * @example
 * validatePermission({
 *   userPermissions: ['admin.read', 'admin.write'],
 *   permissionNameSpace: 'admin',
 *   permissionToValidate: 'read'
 * }); // returns true
 */
export const validatePermission = ({
  userPermissions,
  permissionNameSpace,
  permissionToValidate,
}) => {
  if (isEmpty(userPermissions)) {
    return false;
  }
  // Verify that the permission to validate exists in the PERMISSIONS_ENUM
  if (!Object.values(PERMISSIONS_ENUM).flatMap(Object.values).includes(permissionToValidate)) {
    console.warn(`Unknown permission requested: ${permissionToValidate}`);
    return false;
  }
  // Construct the full permission string to check against user permissions
  const permissionString = `${permissionNameSpace}.${permissionToValidate}`;
  return userPermissions.includes(permissionString);
};

/**
 * Fetches the permissions for the currently authenticated user.
 *
 * This function retrieves user permissions by using the luiPortalUtilitiesManager
 * @returns {Promise<string[]>} A promise that resolves to an array of user permission strings,
 */
export const getUserPermissions = async () => {
  try {
    const data = await luiPortalUtilitiesManager.fetchUserPermissions();
    return data?.permissions || [];
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    return [];
  }
};

export default {
  getUserRoleType,
  validatePermission,
};
